import { useEffect, RefObject, DependencyList } from 'react';
import * as echarts from 'echarts';

// <purpose>Shared ECharts utilities for StatistikSeite charts — lifecycle hook +
// tooltip recipes. Eliminates init/dispose/ResizeObserver boilerplate duplicated
// across every chart file, and the white-tooltip / invisible-tooltip style blobs.</purpose>

/**
 * Chart lifecycle hook: clientWidth>0 guard, echarts.init, ResizeObserver, dispose.
 * The init callback receives the chart instance — call setOption + attach listeners
 * inside it. Return a cleanup fn from init for any non-ECharts teardown (e.g. zrender
 * .off() calls); chart.dispose() handles ECharts-attached listeners automatically.
 */
export function useEChartsLifecycle(
  containerRef: RefObject<HTMLDivElement | null>,
  init: (chart: echarts.ECharts) => (() => void) | void,
  deps: DependencyList,
): void {
  useEffect(() => {
    const el = containerRef.current;
    if (!el || el.clientWidth === 0) return;
    const chart = echarts.init(el);
    const userCleanup = init(chart);
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(el);
    return () => {
      if (typeof userCleanup === 'function') userCleanup();
      ro.disconnect();
      chart.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/** Visible white tooltip — spread into a tooltip config; supply your own formatter + trigger + axisPointer. */
export const STD_TOOLTIP_STYLE = {
  backgroundColor: 'rgba(255,255,255,0.99)',
  borderColor: '#dde0e8',
  borderWidth: 1,
  padding: [8, 12],
  extraCssText: 'box-shadow: 0 2px 8px rgba(6,16,90,0.08); border-radius: 4px;',
} as const;

/** Tooltip that renders nothing — keeps axisPointer.shadow visible without a popup box. */
export const INVISIBLE_TOOLTIP_OPTS = {
  trigger: 'axis',
  axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(6,16,90,0.08)' } },
  formatter: () => '',
  backgroundColor: 'transparent',
  borderColor: 'transparent',
  padding: 0,
  extraCssText: 'box-shadow: none; pointer-events: none; visibility: hidden;',
} as const;

/** Single-series count tooltip formatter — "{binLabel}\n{value} Tickets". */
export function makeCountTooltipFormatter(navy: string) {
  return (params: any) => {
    const p = Array.isArray(params) ? params[0] : params;
    if (!p) return '';
    const label = p.axisValue ?? p.name ?? '';
    const value = p.value ?? p.data?.value ?? '';
    return `<div style="font-size:10px;color:#888">${label}</div><div style="font-size:14px;color:${navy};font-weight:600">${value} <span style="font-size:10px;font-weight:400;color:#888">Tickets</span></div>`;
  };
}
